import type { ReactNode } from 'react';
import { useCallback, useMemo } from 'react';
import { Box, Tooltip } from '@mui/material';
import type { GridColDef, GridRowProps, GridRowSelectionModel, GridValidRowModel } from '@mui/x-data-grid';
import { DataGrid, GridRow } from '@mui/x-data-grid';
import GridInfoFooter from './GridInfoFooter';

export const GROUPED_GRID_PARENT_ROW_CLASS = 'grouped-grid-parent-row';
export const GROUPED_GRID_CHILD_ROW_CLASS = 'grouped-grid-child-row';

interface GroupedSelectableGridProps<TRow extends GridValidRowModel> {
  /** Flattened rows: each parent row followed by its child rows. */
  rows: TRow[];
  columns: GridColDef<TRow>[];
  isChildRow: (row: TRow) => boolean;
  getRowId: (row: TRow) => string;
  selectedIds: string[];
  onSelectionChange: (ids: string[]) => void;
  /** Extra predicate for child rows; parent rows are never selectable. */
  isRowSelectable?: (row: TRow) => boolean;
  /** Tooltip shown while hovering a row (e.g. why a child cannot be selected). */
  getRowTooltip?: (row: TRow) => string | undefined;
  isLoading: boolean;
  footerMessage?: string;
  footer?: ReactNode;
}

const GroupedSelectableGrid = <TRow extends GridValidRowModel>({
  rows,
  columns,
  isChildRow,
  getRowId,
  selectedIds,
  onSelectionChange,
  isRowSelectable,
  getRowTooltip,
  isLoading,
  footerMessage,
  footer,
}: GroupedSelectableGridProps<TRow>) => {
  const handleSelectionModelChange = useCallback(
    (model: GridRowSelectionModel) => {
      onSelectionChange(model.map(String));
    },
    [onSelectionChange],
  );

  const rowSlot = useMemo(() => {
    if (!getRowTooltip) return undefined;

    const RowWithTooltip = (props: GridRowProps) => {
      const title = getRowTooltip(props.row as TRow);
      if (!title) return <GridRow {...props} />;

      return (
        <Tooltip title={title} followCursor>
          <GridRow {...props} />
        </Tooltip>
      );
    };

    return { row: RowWithTooltip };
  }, [getRowTooltip]);

  const footerComponent = useMemo(
    // eslint-disable-next-line react/display-name
    () => () => (footer !== undefined ? <>{footer}</> : <GridInfoFooter showIcon message={footerMessage} />),
    [footer, footerMessage],
  );

  return (
    <Box sx={{ flex: 1, height: '100%', width: '100%', position: 'relative' }}>
      <Box style={{ position: 'absolute', left: 0, top: 0, overflow: 'auto', height: '100%', width: '100%' }}>
        <DataGrid
          rows={rows}
          columns={columns}
          loading={isLoading}
          hideFooterPagination
          rowSelectionModel={selectedIds}
          getRowId={getRowId}
          getRowClassName={(params) =>
            isChildRow(params.row) ? GROUPED_GRID_CHILD_ROW_CLASS : GROUPED_GRID_PARENT_ROW_CLASS
          }
          isRowSelectable={(params) =>
            isChildRow(params.row) && (isRowSelectable?.(params.row) ?? true)
          }
          checkboxSelection
          onRowSelectionModelChange={handleSelectionModelChange}
          sx={{
            [`& .${GROUPED_GRID_PARENT_ROW_CLASS}`]: {
              backgroundColor: '#f5f5f5',
              height: 30,
              '&:hover': {
                backgroundColor: '#f5f5f5',
              },
              '& .MuiDataGrid-cellCheckbox': {
                display: 'none',
              },
            },
            [`& .${GROUPED_GRID_CHILD_ROW_CLASS}:hover`]: {
              backgroundColor: '#fafafa',
            },
          }}
          slots={{ ...rowSlot, footer: footerComponent }}
        />
      </Box>
    </Box>
  );
};

export default GroupedSelectableGrid;
